import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Save, Send } from "lucide-react";
import { Button } from "../ui/button";
import { TopBar } from "../TopBar";
import { SignOutButton } from "../AccountMenu";
import { FullPageLoader } from "../RouteGuards";
import { ProgressSteps } from "./ProgressSteps";
import { WizardCtx } from "./WizardContext";
import { useAuth } from "../../context/AuthContext";
import { supabase } from "../../lib/supabase";
import { emptyData, mergeDraft, buildPayload } from "../../lib/formData";
import { validateStep } from "../../lib/validation";
import StepApplicant from "./steps/StepApplicant";
import StepCandidate from "./steps/StepCandidate";
import StepEducation from "./steps/StepEducation";
import StepFamily from "./steps/StepFamily";
import StepContact from "./steps/StepContact";
import StepPreferences from "./steps/StepPreferences";
import StepUploads from "./steps/StepUploads";
import StepReview from "./steps/StepReview";

const STEPS = [
  { title: "Applicant details", short: "Applicant", desc: "Details of the person filling this form.", C: StepApplicant },
  { title: "Bride / Groom details", short: "Bride/Groom", desc: "Personal and horoscope details of the bride or groom.", C: StepCandidate },
  { title: "Education & occupation", short: "Education", desc: "Qualification and professional details.", C: StepEducation },
  { title: "Family details", short: "Family", desc: "Parents and siblings.", C: StepFamily },
  { title: "Contact details", short: "Contact", desc: "Private addresses and phone numbers.", C: StepContact },
  { title: "Partner preferences & about", short: "Preferences", desc: "What you are looking for, and a few words about you.", C: StepPreferences },
  { title: "Uploads & consent", short: "Uploads", desc: "Photos, documents and your consent.", C: StepUploads },
  { title: "Review & submit", short: "Review", desc: "Please check everything before submitting.", C: StepReview },
];

const scrollToError = () => setTimeout(() => document.querySelector('[data-error="true"]')?.scrollIntoView({ block: "center", behavior: "smooth" }), 60);
const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

export default function Wizard({ onSubmitted, mode = "client", initialProfileFor }) {
  const isAdmin = mode === "admin";
  const { user } = useAuth();
  const userId = user.id;
  const [data, setData] = useState(() => {
    const d = emptyData();
    if (initialProfileFor) d.candidate.profile_for = initialProfileFor;
    return d;
  });
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (isAdmin) { setLoaded(true); return; }
    supabase.from("registration_drafts").select("data,current_step").eq("user_id", userId).maybeSingle().then(({ data: d }) => {
      if (d) { setData(mergeDraft(d.data)); setStep(Math.min(Math.max(d.current_step || 1, 1), STEPS.length)); }
      setLoaded(true);
    });
  }, [userId, isAdmin]);

  const set = useCallback((section, name, value) => {
    setData((p) => ({ ...p, [section]: { ...p[section], [name]: value } }));
    setErrors((e) => { const k = `${section}.${name}`; if (!e[k]) return e; const n = { ...e }; delete n[k]; return n; });
  }, []);

  const saveDraft = async (atStep, silent) => {
    if (isAdmin) return;
    const { error } = await supabase.from("registration_drafts").upsert({ user_id: userId, data, current_step: atStep, email: user.email, signup_method: user.app_metadata?.provider || "email" });
    if (error) toast.error(`Could not save draft: ${error.message}`);
    else if (!silent) toast.success("Draft saved. You can continue any time.");
  };

  const goTo = (n) => { setErrors({}); setStep(n); scrollTop(); };

  const next = () => {
    const errs = validateStep(step, data, { admin: isAdmin });
    setErrors(errs);
    if (Object.keys(errs).length) { toast.error("Please fix the highlighted fields."); scrollToError(); return; }
    const n = step + 1;
    setStep(n);
    saveDraft(n, true);
    scrollTop();
  };

  const submit = async () => {
    for (let s = 1; s <= 7; s += 1) {
      const errs = validateStep(s, data, { admin: isAdmin });
      if (Object.keys(errs).length) { setStep(s); setErrors(errs); toast.error("Some details are missing. Please complete this step."); scrollToError(); return; }
    }
    setBusy("submit");
    const { data: res, error } = await supabase.rpc(isAdmin ? "admin_create_profile" : "submit_registration", { payload: buildPayload(data) });
    setBusy("");
    if (error) { toast.error(error.message); return; }
    toast.success("Registration submitted successfully.");
    onSubmitted({ id: res.profile_id, registration_no: res.registration_no, status: res.status, full_name: data.candidate.full_name });
  };

  const ctx = useMemo(() => ({ data, set, errors, userId, goTo, mode }), [data, set, errors, userId, mode]);
  if (!loaded) return <FullPageLoader />;
  const { title, desc, C } = STEPS[step - 1];
  const isReview = step === STEPS.length;

  return (
    <WizardCtx.Provider value={ctx}>
      <div className={isAdmin ? "" : "rmm-bg min-h-screen pb-28"}>
        {!isAdmin && <TopBar right={<SignOutButton />} />}
        <main className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6 sm:py-10">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C59B27]">Registration form</p>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-[#4A000E] sm:text-4xl">{isAdmin ? "Add new profile (on behalf of a client)" : "Create your matrimonial profile"}</h1>
          <div className="mt-6"><ProgressSteps steps={STEPS.map((s) => s.short)} current={step} /></div>
          <section key={step} data-testid={`wizard-step-${step}`} className="rmm-card rmm-rise mt-6 p-5 sm:p-8">
            <h2 className="font-display text-2xl font-semibold text-[#7A0016]">{title}</h2>
            <p className="mt-1 text-sm text-[#8C6A6C]">{desc} <span className="text-[#A31828]">*</span> marks required fields.</p>
            <div className="gold-rule my-6" />
            <C />
          </section>
        </main>
        <footer className={isAdmin ? "sticky bottom-0 z-30 mx-auto max-w-3xl rounded-xl border border-[#D4AF37]/30 bg-[#FAFAF7]/95 backdrop-blur-md" : "fixed inset-x-0 bottom-0 z-40 border-t border-[#D4AF37]/30 bg-[#FAFAF7]/95 backdrop-blur-md"}>
          <div className="mx-auto flex max-w-3xl items-center gap-2 px-4 py-3 sm:px-6">
            <Button type="button" variant="ghost" disabled={step === 1} onClick={() => goTo(step - 1)} data-testid="wizard-back-button" className="rounded-full text-[#8C6A6C] hover:text-[#7A0016]">
              <ArrowLeft className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Back</span>
            </Button>
            {!isAdmin && (
            <Button type="button" variant="outline" disabled={busy === "draft"} onClick={async () => { setBusy("draft"); await saveDraft(step, false); setBusy(""); }} data-testid="wizard-save-draft-button" className="ml-auto rounded-full border-[#D4AF37] text-[#7A0016] hover:bg-[#F5E6BA]/50">
              {busy === "draft" ? <Loader2 className="h-4 w-4 animate-spin sm:mr-1" /> : <Save className="h-4 w-4 sm:mr-1" />}<span className="hidden sm:inline">Save draft</span><span className="sm:hidden ml-1">Draft</span>
            </Button>
            )}
            {isReview ? (
              <Button type="button" onClick={submit} disabled={busy === "submit"} data-testid="wizard-submit-button" className="rounded-full bg-[#7A0016] px-6 font-semibold text-[#F5E6BA] hover:bg-[#4A000E]">
                {busy === "submit" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}Submit
              </Button>
            ) : (
              <Button type="button" onClick={next} data-testid="wizard-next-button" className="rounded-full bg-[#7A0016] px-6 font-semibold text-[#F5E6BA] hover:bg-[#4A000E]">
                {step === 7 ? "Review" : "Next"}<ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            )}
          </div>
        </footer>
      </div>
    </WizardCtx.Provider>
  );
}
